/**
 * Клиентские проверки ключей перевода — зеркало серверных гардов
 * (`backend/src/domains/translations/services/translationFileContent.ts`).
 *
 * Смысл: не давать пользователю дойти до Save с набором ключей, который
 * сервер всё равно отвергнет (или молча выбросит) при компиляции файла
 * перевода, и показать понятное сообщение до мутации, а не невнятную
 * серверную ошибку после неё.
 */

const DELIMITER = ".";

/**
 * Сегменты, через которые `unflattenObject` на сервере мог бы писать в
 * Object.prototype. Сервер такие ключи отвергает — отвергаем и мы.
 */
const DANGEROUS_SEGMENTS = new Set(["__proto__", "constructor", "prototype"]);

export interface KeyConflict {
  /** Импортируемый ключ, который пришлось отбросить */
  key: string;
  /** Уже принятый ключ, с которым он конфликтует */
  conflict: string;
}

/** true, если ключ содержит опасный сегмент (__proto__/constructor/prototype). */
export function isDangerousTranslationKey(key: string): boolean {
  return key
    .split(DELIMITER)
    .some((segment) => DANGEROUS_SEGMENTS.has(segment));
}

/**
 * Ищет конфликт «строка vs объект» для ключа `candidate` среди `keys`.
 *
 * Ключи `a` и `a.b` не могут сосуществовать: серверный `unflattenObject`
 * попытается положить объект туда, где уже лежит строка, и бросит
 * «parent string», после чего перестаёт компилироваться весь файл перевода.
 *
 * Возвращает конфликтующий ключ (предок или потомок) либо null.
 */
export function findKeyPrefixConflict(
  keys: Iterable<string>,
  candidate: string
): string | null {
  const parts = candidate.split(DELIMITER);
  const ancestors = new Set<string>();

  for (let i = 1; i < parts.length; i += 1) {
    ancestors.add(parts.slice(0, i).join(DELIMITER));
  }

  const childPrefix = `${candidate}${DELIMITER}`;

  for (const key of keys) {
    if (key === candidate) continue;
    if (ancestors.has(key)) return key;
    if (key.startsWith(childPrefix)) return key;
  }

  return null;
}

/**
 * Разделяет импортируемый набор на принимаемые ключи и конфликтующие по
 * точечному префиксу (внутри самого набора и с уже существующими ключами,
 * если они переданы). Порядок обхода — порядок ключей в наборе: первый
 * пришедший ключ выигрывает, последующие конфликтующие отбрасываются.
 */
export function splitConflictingKeys(
  words: Record<string, string>,
  existingKeys: Iterable<string> = []
): { words: Record<string, string>; conflicts: KeyConflict[] } {
  const accepted = new Set<string>();
  // prefix → ключ, ради которого этот префикс появился (для сообщения)
  const prefixOwner = new Map<string, string>();

  const register = (key: string) => {
    accepted.add(key);

    const parts = key.split(DELIMITER);
    for (let i = 1; i < parts.length; i += 1) {
      const prefix = parts.slice(0, i).join(DELIMITER);
      if (!prefixOwner.has(prefix)) prefixOwner.set(prefix, key);
    }
  };

  const findConflict = (key: string): string | null => {
    const parts = key.split(DELIMITER);
    for (let i = 1; i < parts.length; i += 1) {
      const prefix = parts.slice(0, i).join(DELIMITER);
      if (accepted.has(prefix)) return prefix;
    }
    // key оказался строгим префиксом уже принятого ключа
    return prefixOwner.get(key) ?? null;
  };

  for (const key of existingKeys) register(key);

  const result: Record<string, string> = {};
  const conflicts: KeyConflict[] = [];

  Object.entries(words).forEach(([key, value]) => {
    if (accepted.has(key)) {
      // точное совпадение — обычная перезапись значения
      result[key] = value;
      return;
    }

    const conflict = findConflict(key);

    if (conflict) {
      conflicts.push({ key, conflict });
      return;
    }

    register(key);
    result[key] = value;
  });

  return { words: result, conflicts };
}

/** Человекочитаемое сообщение о пропущенных из-за конфликта ключах. */
export function formatKeyConflicts(conflicts: KeyConflict[]): string {
  const shown = conflicts
    .slice(0, 3)
    .map(({ key, conflict }) => `«${key}» ↔ «${conflict}»`)
    .join(", ");
  const rest = conflicts.length > 3 ? ` и ещё ${conflicts.length - 3}` : "";

  return `Пропущено ключей из-за конфликта вложенности: ${conflicts.length} (${shown}${rest})`;
}
